<template>
  <div class="d-flex flex-column flex-grow-1 overflow-hidden">

    <v-sheet color="white" class="flex-shrink-0 pfe-viewer-toolbar d-flex align-center px-3" height="36">
      <template v-if="selectedPage">
        <span class="text-caption text-truncate blue-grey--text text--darken-1">
          {{ selectedPage.docName }} /
          <strong class="primary--text ml-1">{{ $t('Page') }} {{ selectedPage.page.index }}</strong>
        </span>
        <v-spacer />
        <v-btn icon x-small @click="adjustZoom(-0.25)">
          <v-icon x-small>mdi-minus</v-icon>
        </v-btn>
        <v-slider
          v-model="zoomLevel"
          min="0.5" max="3" step="0.25"
          dense hide-details
          class="pfe-zoom-slider"
          @change="renderMainPage"
        />
        <v-btn icon x-small @click="adjustZoom(0.25)">
          <v-icon x-small>mdi-plus</v-icon>
        </v-btn>
        <span class="text-caption primary--text ml-1 pfe-zoom-label">
          {{ Math.round(zoomLevel * 100) }}%
        </span>
      </template>
    </v-sheet>

    <div class="d-flex flex-grow-1 overflow-hidden">

      <v-sheet
        color="grey lighten-2"
        class="flex-grow-1 overflow-auto d-flex align-start justify-center pa-5"
      >
        <div v-if="!selectedPage" class="d-flex flex-column align-center mt-16">
          <v-icon large color="grey lighten-1">mdi-file-pdf-box</v-icon>
          <span class="mt-2 text-caption grey--text">{{ $t('SelectAPageToView') }}</span>
        </div>
        <canvas v-show="selectedPage" ref="mainCanvas" class="pfe-main-canvas" />
      </v-sheet>

      <v-sheet color="grey lighten-4" class="pfe-thumb-sidebar overflow-y-auto flex-shrink-0">
        <template v-if="activeDoc">
          <div
            v-for="page in activeDoc.pages"
            :key="`thumb-${activeDoc.id}-${page.index}`"
            class="pfe-thumb-item"
            :class="{ 'pfe-thumb-item--active': isPageSelected(activeDoc, page) }"
            @click="$emit('select-page', activeDoc, page)"
          >
            <div class="pfe-thumb-canvas-wrap">
              <canvas :ref="`thumb-${activeDoc.id}-${page.index}`" class="pfe-thumb-canvas" />
              <v-icon v-if="isPageSelected(activeDoc, page)" x-small color="primary" class="pfe-thumb-check">
                mdi-check-circle
              </v-icon>
            </div>
            <div class="pfe-thumb-color-bar" :style="{ background: docColorHex[activeDocIndex % docColorHex.length] }" />
            <div class="text-caption text-center blue-grey--text text--lighten-1 pb-1">
              {{ page.index }}
            </div>
          </div>
        </template>
      </v-sheet>

    </div>
  </div>
</template>

<script>
export default {
    name: 'PdfPageViewer',
    props: {
        documents: { type: Array, required: true },
        selectedPage: { type: Object, default: null },
        docColorHex: { type: Array, required: true },
    },
    data() {
        return { zoomLevel: 1.5 };
    },
    computed: {
        activeDoc() {
            if (!this.selectedPage) return null;
            return this.documents.find(d => d.id === this.selectedPage.docId) || null;
        },
        activeDocIndex() {
            if (!this.activeDoc) return 0;
            return this.documents.indexOf(this.activeDoc);
        },
        activeDocPageCount() {
            if (!this.activeDoc) return 0;
            return this.activeDoc.pages.length;
        },
    },
    watch: {
        selectedPage(val) {
            if (val) {
                this.$nextTick(() => {
                    this.renderMainPage();
                    this.scrollThumbIntoView(val.docId, val.page);
                });
            }
        },
        activeDoc(val) {
            if (val) {
                this.$nextTick(() => this.renderThumbnailsForDoc(val));
            }
        },
        activeDocPageCount() {
            if (this.activeDoc) {
                this.$nextTick(() => this.renderThumbnailsForDoc(this.activeDoc));
            }
        },
    },
    methods: {
        isPageSelected(doc, page) {
            return this.selectedPage && this.selectedPage.docId === doc.id && this.selectedPage.page === page;
        },

        async renderMainPage() {
            const canvas = this.$refs.mainCanvas;
            if (!canvas || !this.selectedPage || !this.selectedPage.page.pdfDoc) return;
            try {
                const pdfPage = await this.selectedPage.page.pdfDoc.getPage(this.selectedPage.page.pdfPageNum);
                const viewport = pdfPage.getViewport({ scale: this.zoomLevel });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            } catch (e) {
                return;
            }
        },

        async renderThumbnailsForDoc(doc) {
            await this.$nextTick();
            for (let i = 0; i < doc.pages.length; i++) {
                const page = doc.pages[i];
                const refKey = 'thumb-' + doc.id + '-' + page.index;
                const ref = this.$refs[refKey];
                const canvas = Array.isArray(ref) ? ref[0] : ref;
                if (!canvas || !page.pdfDoc) continue;
                try {
                    const pdfPage = await page.pdfDoc.getPage(page.pdfPageNum);
                    const viewport = pdfPage.getViewport({ scale: 0.2 });
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                } catch (e) {
                    continue;
                }
            }
        },

        adjustZoom(delta) {
            this.zoomLevel = Math.min(3, Math.max(0.5, Math.round((this.zoomLevel + delta) * 4) / 4));
            this.renderMainPage();
        },

        scrollThumbIntoView(docId, page) {
            const refKey = 'thumb-' + docId + '-' + page.index;
            const ref = this.$refs[refKey];
            const canvas = Array.isArray(ref) ? ref[0] : ref;
            if (canvas) canvas.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        },
    },
};
</script>

<style scoped>
.pfe-viewer-toolbar {
  border-bottom: 1px solid #e0e0e0;
}
.pfe-zoom-slider {
  max-width: 120px;
}
.pfe-zoom-label {
  min-width: 2.25rem;
}
.pfe-main-canvas {
  display: block;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
}
.pfe-thumb-sidebar {
  width: clamp(90px, 9vw, 120px);
  flex: 0 0 auto;
  border-left: 1px solid #e0e0e0;
  padding: 6px;
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd transparent;
}
.pfe-thumb-sidebar::-webkit-scrollbar { width: 4px; }
.pfe-thumb-sidebar::-webkit-scrollbar-thumb { background: #bdbdbd; border-radius: 2px; }
.pfe-thumb-item {
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  transition: border-color 0.15s;
  background: #eceff1;
  margin-bottom: 6px;
}
.pfe-thumb-item:hover { border-color: #90a4ae; }
.pfe-thumb-item--active { border-color: var(--v-primary-base); }
.pfe-thumb-canvas-wrap {
  position: relative;
}
.pfe-thumb-canvas {
  display: block;
  width: 100%;
  height: auto;
}
.pfe-thumb-check {
  position: absolute;
  bottom: 2px;
  right: 2px;
}
.pfe-thumb-color-bar {
  height: 3px;
  width: 100%;
}
</style>